import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import firebase from 'firebase/app';
import { ui } from 'util/ui';
import { formatBytes } from 'hooks/useAdminStats';
import useLabelNames, { labelName } from 'hooks/useLabelNames';
import ArchyModal from 'components/shell/ArchyModal';
import { SearchIcon } from 'components/shell/Icons';
import { adminApi, BinKind, LeftoverCounts } from 'util/adminApi';
import { serverQuery } from 'util/serverRead';
import { Panel, SectionHead, TRow, Cell, AdminButton, Pill, Notice } from './adminUi';

/**
 * Admin › Deleted items (Aaron, 5 Nov 2026). Deleting a project or a record no longer leaves its
 * clips, assets and gallery images behind for ever: the server moves them here
 * (platform/functions/src/bin.ts). After BIN_DAYS a nightly job deletes them for good; bucket soft
 * delete then keeps each file another 30 days, restorable from the Google Cloud console.
 */

export const BIN_DAYS = 30;
const KINDS: BinKind[] = ['videos', 'assets', 'gallery'];
const KIND_NAME: Record<BinKind, string> = { videos: 'Clip', assets: 'Asset', gallery: 'Gallery image' };
const REASON: Record<string, string> = {
  'record deleted': 'Record deleted',
  'project deleted': 'Project deleted',
  'left over': 'Left behind before the bin',
};

type BinItem = {
  kind: BinKind;
  id: string;
  name: string;
  size: number;
  category?: string;
  at: Date;
  reason: string;
  projectID?: string;
  projectTitle?: string;
  recordTitle?: string;
};

type Group = { key: string; title: string; note: string; items: BinItem[]; latest: number };

const toItem = (kind: BinKind, doc: firebase.firestore.QueryDocumentSnapshot): BinItem => {
  const d = doc.data();
  const b = d.bin || {};
  return {
    kind,
    id: doc.id,
    name: kind === 'gallery' ? String(d.id || doc.id) : doc.id,
    size: Number(d.size) || 0,
    category: d.category,
    at: b.at && b.at.toDate ? b.at.toDate() : new Date(0),
    reason: b.reason || '',
    projectID: b.projectID || undefined,
    projectTitle: b.projectTitle || undefined,
    recordTitle: b.recordTitle || undefined,
  };
};

const goesOn = (at: Date) => new Date(at.getTime() + BIN_DAYS * 864e5);
const day = (d: Date) => d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
const daysLeft = (at: Date) => Math.max(0, Math.ceil((goesOn(at).getTime() - Date.now()) / 864e5));
const total = (c: LeftoverCounts) => KINDS.reduce((n, k) => n + c[k].files, 0);

/** Pick the project to put things back into, when their own project is gone. */
const ProjectPicker: FC<{ count: number; onClose(): void; onPick(id: string, title: string): void }> = ({ count, onClose, onPick }) => {
  const [search, setSearch] = useState('');
  const [projects, setProjects] = useState<{ id: string; title: string; client: string }[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  useEffect(() => {
    serverQuery(firebase.firestore().collection('projects'))
      .then((snap) =>
        setProjects(
          snap.docs
            .map((d) => ({ id: d.id, title: String(d.get('title') || '').trim(), client: String(d.get('client') || '') }))
            .filter((p) => p.title)
            .sort((a, b) => a.title.localeCompare(b.title)),
        ),
      )
      .catch((e) => setLoadError((e as Error).message));
  }, []);
  const q = search.trim().toLowerCase();
  const shown = (projects || []).filter((p) => !q || `${p.title} ${p.client}`.toLowerCase().includes(q));
  return (
    <ArchyModal
      title="Restore to a project"
      subtitle={`The project ${count === 1 ? 'this file' : `these ${count} files`} came from no longer exists. Choose where to put ${count === 1 ? 'it' : 'them'}.`}
      onClose={onClose}
      width={480}
    >
      <div style={{ padding: '14px 20px 10px', position: 'sticky', top: 0, background: ui.surface, zIndex: 1 }}>
        <label style={{ position: 'relative', display: 'block' }}>
          <span style={{ position: 'absolute', left: 12, top: 12, display: 'flex' }}>
            <SearchIcon size={16} />
          </span>
          <input
            type="text"
            autoFocus
            aria-label="Search projects"
            placeholder="Search projects"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: '100%', height: 40, boxSizing: 'border-box', border: `1px solid ${ui.control}`, borderRadius: 10, padding: '0 12px 0 36px', fontSize: 14, color: ui.ink, background: ui.surface, outline: 'none' }}
          />
        </label>
      </div>
      <div style={{ padding: '0 12px 14px' }}>
        {!projects && (
          <p style={{ margin: 0, padding: '18px 8px', textAlign: 'center', color: loadError ? '#8C1D18' : ui.muted, fontSize: 14 }}>
            {loadError ? `The projects didn't load. ${loadError}` : 'Loading projects…'}
          </p>
        )}
        {projects && !shown.length && <p style={{ margin: 0, padding: '18px 8px', textAlign: 'center', color: ui.muted, fontSize: 14 }}>No projects match "{search}".</p>}
        {shown.slice(0, 300).map((p) => (
          <button
            key={p.id}
            type="button"
            className="archy-row-btn"
            onClick={() => onPick(p.id, p.title)}
            style={{ width: '100%', display: 'block', textAlign: 'left', minHeight: 40, padding: '8px 10px', border: 'none', borderRadius: 10, background: 'transparent', font: 'inherit', fontSize: 14, color: ui.ink, cursor: 'pointer' }}
          >
            <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.title}</span>
            {p.client && <span style={{ display: 'block', fontSize: 12, color: ui.muted }}>{p.client}</span>}
          </button>
        ))}
        {shown.length > 300 && <p style={{ margin: '6px 10px 0', fontSize: 12, color: ui.muted }}>Showing the first 300. Type to narrow the list.</p>}
      </div>
    </ArchyModal>
  );
};

const DeletedItems: FC = () => {
  const names = useLabelNames();
  const [items, setItems] = useState<BinItem[] | null>(null);
  const [leftovers, setLeftovers] = useState<LeftoverCounts | null>(null);
  const [paused, setPaused] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [picker, setPicker] = useState<BinItem[] | null>(null);
  const [open, setOpen] = useState<Record<string, boolean>>({});
  // Set when the list couldn't be read: the screen says so instead of "Nothing here".
  const [listError, setListError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setListError(null);
    try {
      const db = firebase.firestore();
      const snaps = await Promise.all(KINDS.map((k) => serverQuery(db.collection(k).where('bin.at', '>', new Date(0)))));
      setItems(snaps.flatMap((s, i) => s.docs.map((d) => toItem(KINDS[i], d))));
      const settings = await db.collection('settings').doc('bin').get();
      setPaused(settings.get('paused') === true);
    } catch (e) {
      setListError((e as Error).message);
      setItems(null);
    }
    adminApi
      .binLeftovers(true)
      .then((r) => setLeftovers(r.counts))
      .catch((e) => setMessage(`Couldn't count the files with no project: ${(e as Error).message}`));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const groups: Group[] = useMemo(() => {
    const map = new Map<string, Group>();
    (items || []).forEach((it) => {
      const key = it.projectID || '(none)';
      const g =
        map.get(key) ||
        ({
          key,
          title: it.projectTitle || (it.projectID ? 'A deleted project' : 'No project recorded'),
          note: it.projectID && !it.projectTitle ? it.projectID : '',
          items: [],
          latest: 0,
        } as Group);
      if (it.projectTitle && g.title !== it.projectTitle) g.title = it.projectTitle;
      g.items.push(it);
      g.latest = Math.max(g.latest, it.at.getTime());
      map.set(key, g);
    });
    const list = Array.from(map.values());
    list.forEach((g) => g.items.sort((a, b) => a.name.localeCompare(b.name)));
    return list.sort((a, b) => b.latest - a.latest);
  }, [items]);

  const restore = async (list: BinItem[], target?: { id: string; title: string }) => {
    setPicker(null);
    setBusy(list.length === 1 ? list[0].id : `group:${list[0].projectID || '(none)'}`);
    let restored = 0;
    const needs: BinItem[] = [];
    try {
      for (const it of list) {
        const r = await adminApi.binRestore(it.kind, it.id, target && target.id);
        if (r.needsProject) needs.push(it);
        else restored++;
      }
      setMessage(
        restored
          ? `Restored ${restored} file${restored === 1 ? '' : 's'}${target ? ` to “${target.title}”` : ''}.`
          : null,
      );
      if (needs.length) setPicker(needs);
      await load();
    } catch (e) {
      setMessage((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  const fillBin = async () => {
    if (!leftovers) return;
    const n = total(leftovers);
    const when = day(goesOn(new Date()));
    if (!window.confirm(`Move ${n.toLocaleString()} file${n === 1 ? '' : 's'} with no project to Deleted items?\n\nNothing is deleted now. They're deleted for good from ${when} unless you restore them first.`)) return;
    setBusy('fill');
    try {
      const r = await adminApi.binLeftovers(false);
      setMessage(`Moved ${r.binned.toLocaleString()} file${r.binned === 1 ? '' : 's'} to Deleted items. They'll be deleted from ${when}.`);
      await load();
    } catch (e) {
      setMessage((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  const togglePause = async () => {
    const next = !paused;
    if (next === false && !window.confirm(`Turn automatic deletion back on? Anything that has been here ${BIN_DAYS} days or more is deleted tonight.`)) return;
    setBusy('pause');
    try {
      await firebase.firestore().collection('settings').doc('bin').set({ paused: next, updatedAt: firebase.firestore.FieldValue.serverTimestamp() }, { merge: true });
      setPaused(next);
    } catch (e) {
      setMessage((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  const binBytes = (items || []).reduce((n, it) => n + it.size, 0);
  const leftoverTotal = leftovers ? total(leftovers) : 0;
  const leftoverBytes = leftovers ? KINDS.reduce((n, k) => n + leftovers[k].bytes, 0) : 0;
  const COLS = 'minmax(0,3fr) 120px 90px 110px 130px 96px';

  return (
    <section style={{ marginBottom: 28 }}>
      <SectionHead
        title="Deleted items"
        note={`Files from deleted projects and records wait here for ${BIN_DAYS} days, then are deleted for good.`}
      >
        <AdminButton onClick={togglePause} disabled={busy !== null} title="Stops or restarts the nightly deletion">
          {paused ? 'Resume automatic deletion' : 'Pause automatic deletion'}
        </AdminButton>
      </SectionHead>

      {paused && (
        <Notice tone="error">Automatic deletion is paused. Nothing here will be deleted until you resume it.</Notice>
      )}
      {message && <Notice>{message}</Notice>}
      {listError && (
        <Notice tone="error">
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12 }}>
            <span style={{ flex: '1 1 260px' }}>The list didn't load. {listError}</span>
            <AdminButton onClick={load}>Try again</AdminButton>
          </div>
        </Notice>
      )}

      {leftoverTotal > 0 && (
        <Panel padding="14px 16px" style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12 }}>
            <div style={{ flex: '1 1 320px', fontSize: 14, lineHeight: 1.5 }}>
              <strong>
                {leftoverTotal.toLocaleString()} file{leftoverTotal === 1 ? '' : 's'} ({formatBytes(leftoverBytes)}) have no project.
              </strong>{' '}
              They were left behind when projects or records were deleted, before this bin existed:{' '}
              {KINDS.filter((k) => leftovers && leftovers[k].files)
                .map((k) => `${leftovers![k].files.toLocaleString()} ${KIND_NAME[k].toLowerCase()}${leftovers![k].files === 1 ? '' : 's'}`)
                .join(', ')}
              . Moving them here starts their {BIN_DAYS} days.
            </div>
            <AdminButton tone="primary" onClick={fillBin} disabled={busy !== null}>
              {busy === 'fill' ? 'Moving…' : 'Move them to Deleted items'}
            </AdminButton>
          </div>
        </Panel>
      )}

      <div style={{ fontSize: 13, color: ui.muted, margin: '0 0 10px' }}>
        {items === null
          ? listError
            ? 'Not loaded.'
            : 'Loading…'
          : `${items.length.toLocaleString()} file${items.length === 1 ? '' : 's'} · ${formatBytes(binBytes)}. Restore puts a file back where it was, or asks for a project if that one is gone.`}
      </div>

      {items && !items.length && (
        <Panel padding="16px">
          <p style={{ margin: 0, fontSize: 14, color: ui.muted }}>Nothing here. Files arrive when a project or record is deleted.</p>
        </Panel>
      )}

      {groups.map((g) => {
        const expanded = open[g.key] !== false;
        const bytes = g.items.reduce((n, it) => n + it.size, 0);
        const soonest = Math.min(...g.items.map((it) => daysLeft(it.at)));
        return (
          <Panel key={g.key} style={{ marginBottom: 14 }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px 12px', padding: '12px 16px', borderBottom: expanded ? `1px solid ${ui.line}` : 'none' }}>
              <button
                type="button"
                onClick={() => setOpen({ ...open, [g.key]: !expanded })}
                aria-expanded={expanded}
                style={{ flex: '1 1 260px', minWidth: 0, textAlign: 'left', border: 'none', background: 'transparent', padding: 0, font: 'inherit', color: ui.ink, cursor: 'pointer' }}
              >
                <span style={{ fontSize: 15, fontWeight: 700 }}>{expanded ? '▾' : '▸'} {g.title}</span>
                <span style={{ fontSize: 13, color: ui.muted, marginLeft: 8 }}>
                  {g.items.length.toLocaleString()} file{g.items.length === 1 ? '' : 's'} · {formatBytes(bytes)} · first deletion in {soonest} day{soonest === 1 ? '' : 's'}
                  {g.note && ` · ${g.note}`}
                </span>
              </button>
              {g.items.length > 1 && (
                <AdminButton onClick={() => restore(g.items)} disabled={busy !== null}>
                  {busy === `group:${g.key}` ? 'Restoring…' : `Restore all ${g.items.length}`}
                </AdminButton>
              )}
            </div>
            {expanded && (
              <>
                <TRow head columns={COLS}>
                  <Cell>File</Cell>
                  <Cell>Why</Cell>
                  <Cell align="right">Size</Cell>
                  <Cell>Moved here</Cell>
                  <Cell>Deleted from</Cell>
                  <Cell />
                </TRow>
                {g.items.map((it) => (
                  <TRow key={`${it.kind}/${it.id}`} columns={COLS}>
                    <Cell style={{ overflow: 'hidden' }}>
                      <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: 500 }} title={it.name}>
                        {it.name}
                      </div>
                      <div style={{ fontSize: 12, color: ui.muted }}>
                        {KIND_NAME[it.kind]}
                        {it.kind === 'videos' && it.category ? ` · ${labelName(it.category, names)}` : ''}
                        {it.recordTitle ? ` · ${it.recordTitle}` : ''}
                      </div>
                    </Cell>
                    <Cell muted label="Why">{REASON[it.reason] || it.reason}</Cell>
                    <Cell muted label="Size" align="right">{formatBytes(it.size)}</Cell>
                    <Cell muted label="Moved here">{day(it.at)}</Cell>
                    <Cell label="Deleted from">
                      <Pill tone={daysLeft(it.at) <= 3 ? 'amber' : 'grey'}>{paused ? 'Paused' : day(goesOn(it.at))}</Pill>
                    </Cell>
                    <Cell>
                      <AdminButton tone="primary" onClick={() => restore([it])} disabled={busy !== null}>
                        {busy === it.id ? 'Restoring…' : 'Restore'}
                      </AdminButton>
                    </Cell>
                  </TRow>
                ))}
              </>
            )}
          </Panel>
        );
      })}

      {picker && <ProjectPicker count={picker.length} onClose={() => setPicker(null)} onPick={(id, title) => restore(picker, { id, title })} />}
    </section>
  );
};

export default DeletedItems;
